import type { ParsedLosslessFloorPlan } from '../utils/parseLosslessFloorPlan';
import { parseLosslessFloorPlanText } from '../utils/parseLosslessFloorPlan';
import type { DxfParseWorkerRequest, DxfParseWorkerResponse } from './dxfParse.worker';

type Pending = {
  resolve: (value: ParsedLosslessFloorPlan) => void;
  reject: (reason: Error) => void;
};

class DxfParseWorkerClient {
  private worker: Worker | null = null;
  private nextId = 0;
  private pending = new Map<number, Pending>();
  private workerFailed = false;

  private ensureWorker(): Worker | null {
    if (this.workerFailed || typeof Worker === 'undefined') return null;

    if (!this.worker) {
      try {
        this.worker = new Worker(new URL('./dxfParse.worker.ts', import.meta.url), {
          type: 'module',
        });
        this.worker.onmessage = (event: MessageEvent<DxfParseWorkerResponse>) => {
          const data = event.data;
          const entry = this.pending.get(data.id);
          if (!entry) return;
          this.pending.delete(data.id);
          if (!data.ok) {
            entry.reject(new Error(data.error || 'DXF parse worker failed'));
            return;
          }
          entry.resolve(data.result);
        };
        this.worker.onerror = () => {
          this.workerFailed = true;
          this.worker?.terminate();
          this.worker = null;
          for (const [, entry] of this.pending) {
            entry.reject(new Error('DXF parse worker crashed'));
          }
          this.pending.clear();
        };
      } catch {
        this.workerFailed = true;
        return null;
      }
    }

    return this.worker;
  }

  parseLosslessJsonBuffer(buffer: ArrayBuffer, fallbackName: string): Promise<ParsedLosslessFloorPlan> {
    const worker = this.ensureWorker();
    if (!worker) {
      const jsonText = new TextDecoder('utf-8').decode(buffer);
      return new Promise((resolve, reject) => {
        window.setTimeout(() => {
          try {
            resolve(parseLosslessFloorPlanText(jsonText, fallbackName));
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        }, 0);
      });
    }

    const id = ++this.nextId;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      const message: DxfParseWorkerRequest = { id, fallbackName, buffer };
      worker.postMessage(message, [buffer]);
    });
  }
}

export const dxfParseWorkerClient = new DxfParseWorkerClient();
